import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAdminContext } from '../context/AdminContext';
import { useExpertContext } from '../context/ExpertContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiClock, FiCheck, FiX, FiEye, FiDownload, FiLogOut, FiMapPin, FiMail } = FiIcons;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAdminContext();
  const { experts, updateExpertStatus } = useExpertContext();
  
  const [selectedTab, setSelectedTab] = useState('pending');
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [showExpertModal, setShowExpertModal] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
    }
  }, [isAuthenticated, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleApprove = async (expertId) => {
    try {
      await updateExpertStatus(expertId, 'approved');
    } catch (error) {
      console.error('Error approving expert:', error);
    }
  };

  const handleReject = async (expertId) => {
    try {
      await updateExpertStatus(expertId, 'rejected');
    } catch (error) {
      console.error('Error rejecting expert:', error);
    }
  };

  const handleExportCSV = () => {
    const csvData = experts.map(expert => ({
      Name: expert.full_name,
      Email: expert.email,
      Phone: expert.phone || 'N/A',
      Governorate: expert.governorate,
      City: expert.city || 'N/A',
      Status: expert.status,
      'Support Topics': expert.support_topics?.join('; ') || 'N/A',
      'Created At': new Date(expert.created_at).toLocaleDateString()
    }));

    const csvContent = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).map(val => `"${val}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'minecraft-experts.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const pendingExperts = experts.filter(e => e.status === 'pending');
  const approvedExperts = experts.filter(e => e.status === 'approved');
  const rejectedExperts = experts.filter(e => e.status === 'rejected');

  const stats = [
    {
      icon: FiClock,
      label: 'Pending Approval',
      value: pendingExperts.length,
      color: 'text-orange-600',
      bg: 'bg-orange-100'
    },
    {
      icon: FiCheck,
      label: 'Approved',
      value: approvedExperts.length,
      color: 'text-green-600',
      bg: 'bg-green-100'
    },
    {
      icon: FiX,
      label: 'Rejected',
      value: rejectedExperts.length,
      color: 'text-red-600',
      bg: 'bg-red-100'
    },
    {
      icon: FiUsers,
      label: 'Total Applications',
      value: experts.length,
      color: 'text-blue-600',
      bg: 'bg-blue-100'
    }
  ];

  const tabs = [
    { id: 'pending', label: 'Pending', count: pendingExperts.length },
    { id: 'approved', label: 'Approved', count: approvedExperts.length },
    { id: 'rejected', label: 'Rejected', count: rejectedExperts.length }
  ];

  const getExpertsForTab = (tab) => {
    switch (tab) {
      case 'pending': return pendingExperts;
      case 'approved': return approvedExperts;
      case 'rejected': return rejectedExperts;
      default: return [];
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="flex items-center justify-between mb-8"
      >
        <div>
          <h1 className="text-3xl font-bold gradient-text mb-2">Admin Dashboard</h1>
          <p className="text-gray-600">Manage Minecraft Education expert applications</p>
        </div>
        
        <div className="flex items-center space-x-4">
          <button
            onClick={handleExportCSV}
            className="flex items-center space-x-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
          >
            <SafeIcon icon={FiDownload} />
            <span>Export CSV</span>
          </button>
          
          <button
            onClick={handleLogout}
            className="flex items-center space-x-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
          >
            <SafeIcon icon={FiLogOut} />
            <span>Logout</span>
          </button>
        </div>
      </motion.div>

      {/* Stats */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
      >
        {stats.map((stat, index) => (
          <div key={stat.label} className="expert-card text-center">
            <div className={`w-12 h-12 ${stat.bg} rounded-xl flex items-center justify-center mx-auto mb-3`}>
              <SafeIcon icon={stat.icon} className={`text-2xl ${stat.color}`} />
            </div>
            <div className="text-3xl font-bold text-gray-800">{stat.value}</div>
            <div className="text-gray-600 text-sm">{stat.label}</div>
          </div>
        ))}
      </motion.div>

      {/* Tabs */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="expert-card mb-6"
      >
        <div className="flex space-x-1 mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                selectedTab === tab.id
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* Expert List */}
        <div className="space-y-4">
          {getExpertsForTab(selectedTab).length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <SafeIcon icon={FiUsers} className="text-4xl mb-3 mx-auto" />
              <p>No {selectedTab} experts found</p>
            </div>
          ) : (
            getExpertsForTab(selectedTab).map((expert, index) => (
              <motion.div
                key={expert.id}
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4 mb-3">
                      <h3 className="text-lg font-semibold text-gray-800">{expert.full_name}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        expert.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                        expert.status === 'approved' ? 'bg-green-100 text-green-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {expert.status.charAt(0).toUpperCase() + expert.status.slice(1)}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                      <div className="flex items-center space-x-2">
                        <SafeIcon icon={FiMail} className="text-blue-500" />
                        <span>{expert.email}</span>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <SafeIcon icon={FiMapPin} className="text-green-500" />
                        <span>{expert.city}, {expert.governorate}</span>
                      </div>
                    </div>
                    
                    {expert.support_topics && expert.support_topics.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {expert.support_topics.slice(0, 3).map((topic, topicIndex) => (
                          <span
                            key={topicIndex}
                            className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs"
                          >
                            {topic}
                          </span>
                        ))}
                        {expert.support_topics.length > 3 && (
                          <span className="text-xs text-gray-500 px-2 py-1">
                            +{expert.support_topics.length - 3} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => {
                        setSelectedExpert(expert);
                        setShowExpertModal(true);
                      }}
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <SafeIcon icon={FiEye} />
                    </button>
                    
                    {expert.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleApprove(expert.id)}
                          className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                          title="Approve"
                        >
                          <SafeIcon icon={FiCheck} />
                        </button>
                        
                        <button
                          onClick={() => handleReject(expert.id)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                          title="Reject"
                        >
                          <SafeIcon icon={FiX} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </motion.div>

      {/* Expert Detail Modal */}
      {showExpertModal && selectedExpert && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-800">Expert Details</h2>
              <button
                onClick={() => setShowExpertModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiX} className="text-xl text-gray-500" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Personal Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div><strong>Name:</strong> {selectedExpert.full_name}</div>
                  <div><strong>Email:</strong> {selectedExpert.email}</div>
                  <div><strong>Phone:</strong> {selectedExpert.phone || 'Not provided'}</div>
                  <div><strong>Location:</strong> {selectedExpert.city}, {selectedExpert.governorate}</div>
                </div>
              </div>
              
              {selectedExpert.bio && (
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">Bio</h3>
                  <p className="text-gray-600">{selectedExpert.bio}</p>
                </div>
              )}
              
              {selectedExpert.support_topics && selectedExpert.support_topics.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">Support Topics</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedExpert.support_topics.map((topic, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                      >
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex items-center space-x-4 pt-4 border-t border-gray-200">
                <div><strong>Status:</strong> 
                  <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                    selectedExpert.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                    selectedExpert.status === 'approved' ? 'bg-green-100 text-green-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {selectedExpert.status.charAt(0).toUpperCase() + selectedExpert.status.slice(1)}
                  </span>
                </div>
                <div><strong>Applied:</strong> {new Date(selectedExpert.created_at).toLocaleDateString()}</div>
              </div>
              
              {selectedExpert.status === 'pending' && (
                <div className="flex space-x-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      handleApprove(selectedExpert.id);
                      setShowExpertModal(false);
                    }}
                    className="flex-1 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiCheck} />
                    <span>Approve Expert</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      handleReject(selectedExpert.id);
                      setShowExpertModal(false);
                    }}
                    className="flex-1 bg-red-600 text-white px-4 py-3 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiX} />
                    <span>Reject Expert</span>
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;